import { useChatStore } from '@/stores/chat.store';
import { useThreadStore } from '@/stores/thread.store';
import { useAuthStore } from '@/stores/auth.store';

// Stable empty arrays prevent Zustand selector from returning new refs on every store change.
// Without these, `?? []` creates a new array each time → Object.is sees a change →
// useSyncExternalStore triggers infinite synchronous re-renders → React Error #185.
const EMPTY_TYPERS: { userId: string; displayName: string; profilePictureUrl: string | null }[] = [];

interface TypingIndicatorProps {
  areaId: string;
  threadId?: string;
}

export function TypingIndicator({ areaId, threadId }: TypingIndicatorProps) {
  const areaTypers = useChatStore((s) => s.typingByArea[areaId] ?? EMPTY_TYPERS);
  const threadTypers = useThreadStore((s) => (threadId ? s.typingByThread[threadId] ?? EMPTY_TYPERS : EMPTY_TYPERS));
  const currentUserId = useAuthStore((s) => s.user?.id);

  const typers = threadId ? threadTypers : areaTypers;

  // Filter out self
  const otherTypers = typers.filter((t) => t.userId !== currentUserId);

  if (otherTypers.length === 0) return null;

  const names = otherTypers.map((t) => t.displayName);
  let text = '';
  if (names.length === 1) {
    text = `${names[0]} is typing`;
  } else if (names.length === 2) {
    text = `${names[0]} and ${names[1]} are typing`;
  } else {
    text = `${names[0]} and ${names.length - 1} others are typing`;
  }

  return (
    <div role="status" aria-live="polite" className="flex items-center gap-2 px-4 py-1">
      <div className="flex -space-x-1">
        {otherTypers.slice(0, 3).map((t) => (
          <div
            key={t.userId}
            className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-white bg-blue-100 text-[10px] font-medium text-blue-700 dark:border-gray-900 dark:bg-blue-900 dark:text-blue-300"
          >
            {t.profilePictureUrl ? (
              <img
                src={t.profilePictureUrl}
                alt=""
                className="h-5 w-5 rounded-full object-cover"
              />
            ) : (
              t.displayName.charAt(0).toUpperCase()
            )}
          </div>
        ))}
      </div>
      <span className="text-xs text-gray-600 dark:text-gray-300">
        {text}
        <span className="ml-0.5 inline-flex gap-0.5">
          <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
          <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
          <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
        </span>
      </span>
    </div>
  );
}
